import { Fragment, useEffect } from "react";

import clsx from "clsx";

import Container from "@Components/Container";
import type { IAjax } from "@Components/Hooks/useAjax";
import useAjax from "@Components/Hooks/useAjax";
import type { ILink } from "@Components/Link";
import { Link, LinkTarget } from "@Components/Link";
import { Loading } from "@Components/Loading";
import { Panel } from "@Components/Panel";
import type { IRepeater } from "@Components/Repeater";
import { ItemTemplate, NoResultsTemplate, Repeater } from "@Components/Repeater";

import type { IContentContainer } from "../common/ContentContainer";
import ContentContainer from "../common/ContentContainer";
import { TransitionLink } from "../common/Transition";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./SitemapIndex.module.css";

export interface ISitemapIndex extends IAjax<ISitemapIndexItem[]> {
    sections: ISitemapIndexItem[];
    contentContainers?: IContentContainer;
    title?: string;
}

interface ISitemapIndexItem {
    itemTitle: string;
    links: IRepeater;
}

interface ISitemapLink extends ILink {
    isSubLink: boolean;
}

const SitemapIndex = (props: ISitemapIndex) => {
    const { isLoading, data, request } = useAjax<ISitemapIndexItem[]>(props);

    useEffect(() => {
        if (!props.initialData) {
            request();
        }
    }, []);

    const renderContent = () => {
        if (isLoading === true || !data) {
            return <Loading />;
        }

        return data.map((value, index) => (
            <Fragment key={index}>
                <div key={index} className={css.column}>
                    <Panel title={value.itemTitle} titleElement="h4">
                        <Repeater {...value.links}>
                            <ItemTemplate>
                                {(compProps: ISitemapLink) => {
                                    compProps.className = compProps.isSubLink ? css.subLink : "";

                                    return compProps.target === LinkTarget.Blank ? (
                                        <Link {...compProps} target={LinkTarget.Self} />
                                    ) : (
                                        <TransitionLink {...compProps} />
                                    );
                                }}
                            </ItemTemplate>
                            <NoResultsTemplate />
                        </Repeater>
                    </Panel>
                </div>
            </Fragment>
        ));
    };

    return (
        <Container>
            <div className={clsx(css.sitemapIndexComponent, cssPortal.standardContainer)}>
                <h1>{props.title}</h1>
                {renderContent()}
                <ContentContainer {...props.contentContainers} />
            </div>
        </Container>
    );
};

export { SitemapIndex };
